<template>
  <div>
    <div class="flex justify-between">
      <h2 class="font-bold text-18px pb-0.5">Creatives</h2>
      <div class="flex items-center gap-1">
        <ElButton
          v-if="canRequestRevisionForMultiple"
          type="primary"
          icon="el-icon-s-promotion"
          size="small"
          class="text-sm font-medium"
          @click.stop="sendAllToReview"
          >Send all to review</ElButton
        >
        <!-- PD-3985 — shown whenever the flight has creatives, disabled with an
             explanation when none can be downloaded yet, so a TM sees why rather
             than no button at all. The span keeps the tooltip working on a
             disabled button, which swallows mouse events. -->
        <ElTooltip
          v-if="hasCreatives"
          :disabled="hasDownloadableCreatives"
          content="Only approved creatives with uploaded files can be downloaded"
          placement="top"
        >
          <span>
            <ElButton
              type="secondary"
              icon="el-icon-download"
              size="small"
              class="text-sm font-medium bg-primary/10 text-primary"
              :loading="isDownloading"
              :disabled="isDownloading || !hasDownloadableCreatives"
              @click.stop="downloadAllCreativeFiles"
              >Download all</ElButton
            >
          </span>
        </ElTooltip>
      </div>
    </div>

    <div v-loading="loading">
      <el-switch v-model="allCollapse" class="mb-1" active-text="Expand" inactive-text="Collapse"> </el-switch>
      <template v-if="listCreatives?.length">
        <div v-for="(asset, index) in listCreatives" :key="index">
          <div class="bg-white rounded-md border border-grayNeutralLight p-2 mb-0.5">
            <div class="flex items-center gap-1 w-full p-1">
              <div class="flex items-center gap-1 cursor-pointer" @click="toggle(index)">
                <template v-if="asset.creatives.length">
                  <i :class="isOpen(index) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
                  <svg v-if="icon" width="14" height="16">
                    <use :xlink:href="`#${icon}`"></use>
                  </svg>
                  <svg width="16" height="17">
                    <use :xlink:href="`#files`"></use>
                  </svg>
                </template>
                <h3 class="font-bold text-dark mb-0">{{ asset.name }}</h3>
                <span
                  v-if="asset.creatives.length"
                  class="w-3.5 h-3.5 text-[#909399] bg-grayLightest inline-flex justify-center items-center rounded-full font-bold"
                >
                  {{ asset.creatives.length }}
                </span>
              </div>
              <ElButton
                v-if="asset.creatives.length && canUploadCreative()"
                :disabled="!canUploadCreative()"
                type="secondary"
                class="ml-auto bg-primary/10 text-primary button-custom-icon"
                @click="uploadCreative('upload-creatives', asset)"
              >
                <svg width="16" height="16">
                  <use :xlink:href="`#upload-arrow`"></use>
                </svg>
                Upload
              </ElButton>
            </div>

            <div v-if="asset.creatives.length" v-show="isOpen(index)">
              <CreativeCard
                v-for="(creative, index) in asset.creatives"
                :key="index"
                :creative="creative"
                :flight-status="flightStatus"
                @delete="openDeleteModal"
              />
            </div>

            <!-- No Creatives Found -->
            <div v-else>
              <NoDataCard>
                <template slot="title">No creatives</template>
                <template slot="info">
                  <template v-if="canUploadCreative() && asset.creatives?.length === 0">
                    <p class="mb-0">Start by uploading creatives for this campaign</p>
                    <ElButton
                      v-if="canUploadCreative()"
                      :disabled="!canUploadCreative()"
                      type="primary"
                      icon="el-icon-upload"
                      class="mt-2"
                      @click="uploadCreative('upload-creatives', asset)"
                    >
                      Upload
                    </ElButton>
                  </template>
                  <p v-else>Creatives will be displayed here once they’re ready.</p>
                </template>
              </NoDataCard>
            </div>
          </div>
        </div>
      </template>

      <NoDataCard v-else>
        <template slot="title">No creatives found</template>
        <template slot="info">Creatives will be displayed here once they’re ready.</template>
      </NoDataCard>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import NoDataCard from '@/components/social-products/social-boost/parts/NoDataCard.vue'
import UploadCreatives from '@/components/social-products/social-boost/modal/UploadCreatives.vue'
import DeleteCreative from '@/components/social-products/social-boost/modal/creative/DeleteCreative.vue'
import CreativeCard from '@/components/social-products/social-boost/parts/CreativeCard.vue'
import CreativesMixin from '@/mixins/social-products/Creatives.js'
import SendToReview from '@/components/social-products/social-boost/modal/creative/SendToReview.vue'
import Approve from '@/components/social-products/social-boost/modal/creative/Approve.vue'
import CreativeModal from '@/components/social-products/social-boost/modal/creative/CreativeModal.vue'
import LayoutModalSlider from '@/components/social-products/social-boost/modal/creative/LayoutSlider.vue'

export default {
  components: {
    NoDataCard,
    UploadCreatives,
    DeleteCreative,
    CreativeCard,
  },
  mixins: [CreativesMixin],
  props: {
    icon: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      openStates: {},
      allCollapse: false,
    }
  },
  watch: {
    allCollapse: function (value) {
      this.listCreatives.forEach((creative, index) => {
        this.$set(this.openStates, index, value)
      })
    },
  },
  created() {
    this.init()
    this.$Modal.bus.$on('reload:creatives', this.init)
  },
  destroyed() {
    this.$Modal.bus.$off('reload:creatives', this.init)
  },
  computed: {
    ...mapGetters('socialBoostCampaign', ['campaign', 'creatives']),
    flight() {
      return this.campaign?.flights?.find((f) => f.id === Number(this.$route.params.flight_id))
    },
    listCreatives() {
      return this.creatives?.list || []
    },
    hasCreatives() {
      return this.listCreatives.some((asset) => (asset.creatives || []).length > 0)
    },
    // PD-3985 — mirrors what download_all() actually puts in the archive: approved
    // creatives that have at least one stored file. Otherwise the button is
    // disabled with an explanation rather than offered and answered with a 422.
    hasDownloadableCreatives() {
      return this.listCreatives.some((asset) =>
        (asset.creatives || []).some((creative) => this.canDownload(creative.status) && (creative.files || []).some((file) => file.path))
      )
    },
    flightStatus() {
      return this.creatives?.flight_status ?? -1
    },
    canRequestRevisionForMultiple() {
      const allowedFlightStatuses = ['in_progress']
      const allowedCreativeStatuses = ['new']

      return (
        this.$is_GD &&
        this.$SBCampaignStatusAccess?.(allowedFlightStatuses, this.flightStatus) &&
        this.listCreatives.some((item) => {
          return item.creatives.some((creative) => {
            return this.$SBCampaignStatusAccess?.(allowedCreativeStatuses, creative.status)
          })
        })
      )
    },
  },
  methods: {
    toggle(index) {
      this.$set(this.openStates, index, !this.openStates[index])
    },
    isOpen(index) {
      return this.openStates[index] || false
    },
    async init() {
      this.loading = true
      await this.$store.dispatch('socialBoostCampaign/getCreatives', { flight_id: this.$route.params.flight_id })
      if (this.listCreatives.length === 1) {
        this.$set(this.openStates, 0, true)
        this.allCollapse = true
      }
      this.loading = false
      this.openCreativeFromRoute()
    },

    /**
     * Opens the creative targeted by an actionable notification
     * (…/creatives?creative=123) and expands the asset holding it.
     */
    openCreativeFromRoute() {
      const creativeId = Number(this.$route.query.creative)
      if (!creativeId) return

      const assetIndex = this.listCreatives.findIndex((asset) => asset.creatives?.some((creative) => creative.id === creativeId))
      if (assetIndex === -1) return

      const creative = this.listCreatives[assetIndex].creatives.find((item) => item.id === creativeId)
      this.$set(this.openStates, assetIndex, true)

      // consume the param so reloading the tab doesn't reopen the modal
      const query = { ...this.$route.query }
      delete query.creative
      this.$router.replace({ query }).catch(() => {})

      this.$Modal.init(
        {
          propsData: {
            component: CreativeModal,
            meta: {
              creative_id: creative.id,
              flightStatus: this.flightStatus,
              openChat: true,
              // The same callbacks CreativeCard passes when it opens this modal.
              // Without them CreativeModal resolves both to a no-op and the
              // Approve / Send-to-review buttons render enabled but do nothing —
              // for exactly the reviewers this deep link exists for.
              sendToReview: () => this.openCreativeActionModal(SendToReview, creative),
              approve: () => this.openCreativeActionModal(Approve, creative),
            },
            size: '1200px',
          },
          layout: LayoutModalSlider,
          callback: (payload) => {
            if (payload.event === 'modal:close') {
              this.$Modal.bus.$emit('reload:creatives')
            }
          },
        },
        this
      )
    },

    /** Opens the Approve / Send-to-review confirmation, as CreativeCard does. */
    openCreativeActionModal(component, creative) {
      this.$Modal.init(
        {
          propsData: {
            component,
            meta: { creative },
            size: '480px',
          },
          callback() {},
        },
        this
      )
    },

    uploadCreative(command, asset) {
      if (command === 'upload-creatives') {
        this.$Modal.init(
          {
            propsData: {
              component: UploadCreatives,
              meta: {
                asset,
              },
              size: '480px',
            },
            callback: async (payload) => {
              if (payload.event === 'modal:close') {
              }
            },
          },
          this
        )
      }
    },
    openDeleteModal(creative) {
      this.$Modal.init(
        {
          propsData: {
            component: DeleteCreative,
            meta: { creative },
            size: '480px',
          },
        },
        this
      )
    },
    sendAllToReview() {
      this.$Modal.init(
        {
          propsData: {
            component: SendToReview,
            meta: { multiple: true, flight_id: this.flight.id },
            size: '480px',
          },
          callback(payload) {
            if (payload.event === 'modal:close') {
            }
          },
        },
        this
      )
    },
  },
}
</script>
<style lang="scss">
.button-custom-icon {
  span {
    display: flex;
  }
}
</style>
